(() => {
  'use strict';

  const scene = document.querySelector('.story-scene--autumn[data-story-reveal]');
  const heroPaper = scene?.querySelector('.paper--hero');
  const portalEntry = scene?.querySelector('[data-v86-portal-entry]');
  const dialog = document.querySelector('[data-v86-review-dialog]');
  const openButton = portalEntry?.querySelector('[data-v86-review-open]');
  const closeButton = dialog?.querySelector('[data-v86-review-close]');

  if (!scene || !heroPaper || !portalEntry || !dialog || !openButton || !closeButton || typeof dialog.showModal !== 'function') return;

  let returnFocusTo = null;

  const syncRevealState = () => {
    const isPainting = scene.dataset.revealInteraction === 'marker-coloring';
    scene.classList.toggle('is-v86-painting', isPainting);
    if (
      (isPainting || scene.classList.contains('is-reveal-pointing')) &&
      !scene.classList.contains('has-v86-painted')
    ) {
      scene.classList.add('has-v86-painted');
    }
  };

  const syncPortalGeometry = () => {
    if (heroPaper.classList.contains('is-minimized') || heroPaper.hasAttribute('aria-busy')) return;
    const gap = window.innerWidth < 360 ? 18 : Math.min(34, Math.max(20, window.innerHeight * 0.04));
    portalEntry.style.setProperty('--v86-portal-top', `${Math.round(heroPaper.offsetTop + heroPaper.offsetHeight + gap)}px`);
    portalEntry.style.setProperty('--v86-portal-left', `${heroPaper.offsetLeft}px`);
    portalEntry.style.setProperty('--v86-portal-width', `${heroPaper.getBoundingClientRect().width}px`);
    scene.style.setProperty('--v86-scene-height', `${Math.ceil(heroPaper.offsetTop + heroPaper.offsetHeight + gap + portalEntry.offsetHeight + 180)}px`);
  };

  const focusableElements = () =>
    Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(
      (element) => element.getClientRects().length > 0 && !element.hasAttribute('hidden'),
    );

  const openDialog = () => {
    if (dialog.open) return;
    returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : openButton;
    scene.classList.add('is-v86-portal-open');
    dialog.showModal();
    dialog.querySelector('[data-v86-review-sheet]').scrollTop = 0;
    document.body.classList.add('v86-review-open');
    openButton.setAttribute('aria-expanded', 'true');
    closeButton.focus({ preventScroll: true });
  };

  const closeDialog = () => {
    if (dialog.open) dialog.close();
  };

  const revealObserver = new MutationObserver(syncRevealState);
  revealObserver.observe(scene, {
    attributes: true,
    attributeFilter: ['class', 'data-reveal-interaction'],
  });
  syncRevealState();
  syncPortalGeometry();

  const portalResizeObserver = 'ResizeObserver' in window ? new ResizeObserver(syncPortalGeometry) : null;
  portalResizeObserver?.observe(heroPaper);
  portalResizeObserver?.observe(portalEntry);
  const heroObserver = new MutationObserver(syncPortalGeometry);
  heroObserver.observe(heroPaper, {attributes: true, attributeFilter: ['class', 'aria-busy']});
  document.fonts?.ready.then(syncPortalGeometry);
  window.addEventListener('resize', syncPortalGeometry, { passive: true });

  ['pointerdown', 'pointerup', 'pointercancel'].forEach((eventName) => {
    openButton.addEventListener(eventName, (event) => event.stopPropagation());
  });
  openButton.addEventListener('click', openDialog);
  closeButton.addEventListener('click', closeDialog);

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) closeDialog();
  });

  dialog.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;

    const focusable = focusableElements();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !dialog.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  });

  dialog.addEventListener('close', () => {
    document.body.classList.remove('v86-review-open');
    scene.classList.remove('is-v86-portal-open');
    openButton.setAttribute('aria-expanded', 'false');

    const focusTarget = returnFocusTo;
    returnFocusTo = null;
    if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
  });

  window.addEventListener('pagehide', (event) => {
    if (event.persisted) return;
    revealObserver.disconnect();
    portalResizeObserver?.disconnect();
    heroObserver.disconnect();
    window.removeEventListener('resize', syncPortalGeometry);
  });
  window.addEventListener('pageshow', (event) => { if (event.persisted) { syncPortalGeometry(); syncRevealState(); } });
})();



/* Dependency-free, visibility-aware optional photo carousel. */
(() => {
  const carousel = document.querySelector('[data-v86-carousel]');
  if (!carousel) return;
  const scene = carousel.closest('[data-story-reveal]');
  const hero = scene.querySelector('.paper--hero');
  const dialog = document.querySelector('[data-v86-review-dialog]');
  const slides = [...carousel.querySelectorAll('[data-v86-slide]')];
  const previous = carousel.querySelector('[data-v86-previous]');
  const next = carousel.querySelector('[data-v86-next]');
  const toggle = carousel.querySelector('[data-v86-autoplay]');
  const label = toggle.querySelector('span');
  const counter = carousel.querySelector('[data-v86-position]');
  const live = carousel.querySelector('[data-v86-announcement]');
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection;
  let current = 0, visible = false, timer = null, busy = false;
  let userPaused = motion.matches || Boolean(connection?.saveData);
  let disposed = false;
  const load = image => {
    if (image.dataset.srcset) { image.srcset = image.dataset.srcset; delete image.dataset.srcset; }
    if (image.dataset.src) { image.src = image.dataset.src; delete image.dataset.src; }
    return image.decode().catch(() => null);
  };
  const stopped = () => disposed || userPaused || motion.matches || connection?.saveData || !visible ||
    document.hidden || Boolean(document.querySelector('dialog[open]')) || hero.classList.contains('is-minimized') ||
    scene.dataset.revealInteraction === 'marker-coloring';
  const clear = () => { if (timer !== null) clearTimeout(timer); timer = null; };
  const renderToggle = () => {
    const paused = userPaused || motion.matches || Boolean(connection?.saveData);
    label.textContent = paused ? 'Play slideshow' : 'Pause slideshow';
    toggle.setAttribute('aria-label', paused ? 'Play photo slideshow' : 'Pause photo slideshow');
    toggle.setAttribute('aria-pressed', String(!paused));
    toggle.querySelector('[data-v86-play-icon]').toggleAttribute('hidden', !paused);
    toggle.querySelector('[data-v86-pause-icon]').toggleAttribute('hidden', paused);
  };
  const schedule = () => {
    clear(); renderToggle();
    if (!stopped()) timer = setTimeout(() => show(current + 1, false), 6000);
  };
  async function show(index, manual) {
    if (busy || disposed) return;
    if (manual) { userPaused = true; clear(); }
    const target = (index + slides.length) % slides.length;
    busy = true;
    await load(slides[target]);
    if (!disposed && (manual || !stopped()) && slides[target].naturalWidth) {
      slides[current].hidden = true; slides[target].hidden = false; current = target;
      carousel.dataset.v86Current = String(current);
      counter.textContent = (current + 1) + ' / ' + slides.length;
      counter.setAttribute('aria-label', 'Photo ' + (current + 1) + ' of ' + slides.length);
      if (manual) live.textContent = 'Photo ' + (current + 1) + ' of ' + slides.length + ': ' + slides[current].alt;
    }
    busy = false; schedule();
  }
  const pause = () => { userPaused = true; schedule(); };
  previous.addEventListener('click', () => show(current - 1, true));
  next.addEventListener('click', () => show(current + 1, true));
  toggle.addEventListener('click', () => {
    // Explicit play never overrides Reduced Motion or Save Data preferences.
    if (motion.matches || connection?.saveData) { userPaused = true; live.textContent = 'Automatic rotation is off for your device preferences. Use the photo arrows.'; }
    else userPaused = !userPaused;
    schedule();
  });
  carousel.addEventListener('focusin', event => { if (!toggle.contains(event.target)) pause(); });
  carousel.addEventListener('pointerdown', event => { if (!toggle.contains(event.target)) pause(); event.stopPropagation(); });
  carousel.addEventListener('pointerup', event => event.stopPropagation());
  carousel.addEventListener('pointercancel', event => event.stopPropagation());
  const media = carousel.querySelector('.v86-portal-entry__media');
  let gesture = null;
  media.addEventListener('pointerdown', event => {
    if (event.pointerType !== 'touch' || !event.isPrimary) return;
    gesture = {id:event.pointerId, x:event.clientX, y:event.clientY};
    media.setPointerCapture(event.pointerId);
  });
  media.addEventListener('pointerup', event => {
    if (!gesture || gesture.id !== event.pointerId) return;
    const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
    gesture = null;
    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) show(current + (dx < 0 ? 1 : -1), true);
  });
  media.addEventListener('pointercancel', () => { gesture = null; });
  const visibility = new IntersectionObserver(entries => {
    visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .5; schedule();
  }, { threshold: [0, .5] });
  visibility.observe(carousel);
  const nearby = new IntersectionObserver(entries => {
    if (entries[0].isIntersecting) {
      if (!connection?.saveData) slides.slice(1).forEach(load);
      nearby.disconnect();
    }
  }, { rootMargin: '180px' });
  nearby.observe(carousel);
  const state = new MutationObserver(schedule);
  state.observe(hero, {attributes:true, attributeFilter:['class']});
  state.observe(scene, {attributes:true, attributeFilter:['data-reveal-interaction']});
  document.querySelectorAll('dialog').forEach(modal => state.observe(modal, {attributes:true, attributeFilter:['open']}));
  document.addEventListener('visibilitychange', schedule);
  const preference = () => { if (motion.matches || connection?.saveData) userPaused = true; schedule(); };
  motion.addEventListener('change', preference);
  connection?.addEventListener?.('change', preference);
  renderToggle();
  window.addEventListener('pagehide', event => {
    clear();
    if (event.persisted) return;
    disposed = true; clear(); visibility.disconnect(); nearby.disconnect(); state.disconnect();
    document.removeEventListener('visibilitychange', schedule);
    motion.removeEventListener('change', preference);
    connection?.removeEventListener?.('change', preference);
  });
  window.addEventListener('pageshow', event => { if (event.persisted) schedule(); });
})();
